import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  getDashboardStats,
  getConferences,
  getBooks,
  getTrainings,
  getConferenceRegistrations,
  getBookPurchases,
  getTrainingRegistrations,
  getAllUsers,
  createConference,
  updateConference,
  deleteConference,
  createBook,
  updateBook,
  deleteBook,
  createTraining,
  updateTraining,
  deleteTraining,
  createUser,
  updateUser,
  deleteUser,
  updateConferencePaymentStatus,
  updateBookPaymentStatus,
  updateTrainingPaymentStatus,
} from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import {
  DollarSign,
  Users,
  Calendar,
  BookOpen,
  GraduationCap,
  BarChart3,
  LogOut,
  Home,
  Download,
  Plus,
  Edit,
  Trash2,
  Check,
  X,
} from 'lucide-react';
import { useToast } from '../../hooks/use-toast';
import { ConferencesManager } from '../../components/admin/ConferencesManager';
import { BooksManager } from '../../components/admin/BooksManager';
import { TrainingsManager } from '../../components/admin/TrainingsManager';
import { UsersManager } from '../../components/admin/UsersManager';

export const EnhancedDashboard = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const { toast } = useToast();
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');
  
  // Data states
  const [conferences, setConferences] = useState([]);
  const [books, setBooks] = useState([]);
  const [trainings, setTrainings] = useState([]);
  const [confRegistrations, setConfRegistrations] = useState([]);
  const [bookPurchases, setBookPurchases] = useState([]);
  const [trainingRegistrations, setTrainingRegistrations] = useState([]);
  const [users, setUsers] = useState([]);
  
  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    if (!user || user.role !== 'admin') {
      navigate('/admin-login');
      return;
    }
    loadAllData();
  }, [user, navigate]);

  const loadAllData = async () => {
    try {
      const [
        statsRes,
        confRes,
        booksRes,
        trainingsRes,
        confRegRes,
        bookPurchRes,
        trainRegRes,
        usersRes,
      ] = await Promise.all([
        getDashboardStats(),
        getConferences(),
        getBooks(),
        getTrainings(),
        getConferenceRegistrations(),
        getBookPurchases(),
        getTrainingRegistrations(),
        getAllUsers(),
      ]);
      
      setStats(statsRes.data);
      setConferences(confRes.data);
      setBooks(booksRes.data);
      setTrainings(trainingsRes.data);
      setConfRegistrations(confRegRes.data);
      setBookPurchases(bookPurchRes.data);
      setTrainingRegistrations(trainRegRes.data);
      setUsers(usersRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      toast({
        title: 'Error',
        description: 'Failed to load dashboard data',
        variant: 'destructive',
      });
    }
  };

  const exportToCSV = (data, filename) => {
    if (data.length === 0) return;
    
    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(','),
      ...data.map(row => 
        headers.map(header => 
          JSON.stringify(row[header] || '')
        ).join(',')
      )
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    
    toast({
      title: 'Success',
      description: `${filename} exported successfully`,
    });
  };

  const handleUpdatePaymentStatus = async (type, id, status) => {
    try {
      if (type === 'conference') {
        await updateConferencePaymentStatus(id, status);
      } else if (type === 'book') {
        await updateBookPaymentStatus(id, status);
      } else if (type === 'training') {
        await updateTrainingPaymentStatus(id, status);
      }
      
      await loadAllData();
      toast({
        title: 'Success',
        description: 'Payment status updated',
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update payment status',
        variant: 'destructive',
      });
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/admin-login');
  };

  if (!stats) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-xl">Loading dashboard...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
            <p className="text-sm text-gray-600">Welcome, {user?.full_name || user?.email}</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => navigate('/')}>
              <Home className="w-4 h-4 mr-2" />
              View Site
            </Button>
            <Button variant="destructive" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Logout
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Revenue</CardTitle>
              <DollarSign className="h-4 w-4 text-green-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">₦{stats.total_revenue.toLocaleString()}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Total Users</CardTitle>
              <Users className="h-4 w-4 text-blue-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_users}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Registrations</CardTitle>
              <Calendar className="h-4 w-4 text-purple-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.total_registrations}</div>
            </CardContent>
          </Card>
          <Card>
            <CardHeader className="flex flex-row items-center justify-between pb-2">
              <CardTitle className="text-sm font-medium">Book Sales</CardTitle>
              <BookOpen className="h-4 w-4 text-orange-600" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.book_sales}</div>
            </CardContent>
          </Card>
        </div>

        {/* Main Content Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="grid w-full grid-cols-6">
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="conferences">Conferences</TabsTrigger>
            <TabsTrigger value="books">Books</TabsTrigger>
            <TabsTrigger value="trainings">Trainings</TabsTrigger>
            <TabsTrigger value="registrations">Registrations</TabsTrigger>
            <TabsTrigger value="users">Users</TabsTrigger>
          </TabsList>

          {/* Overview Tab */}
          <TabsContent value="overview">
            <Card>
              <CardHeader>
                <CardTitle>Dashboard Overview</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="border rounded p-4">
                      <h3 className="font-semibold mb-2">Active Conferences</h3>
                      <p className="text-3xl font-bold text-blue-600">{stats.active_conferences}</p>
                    </div>
                    <div className="border rounded p-4">
                      <h3 className="font-semibold mb-2">Training Enrollments</h3>
                      <p className="text-3xl font-bold text-green-600">{stats.training_enrollments}</p>
                    </div>
                    <div className="border rounded p-4">
                      <h3 className="font-semibold mb-2">Pending Payments</h3>
                      <p className="text-3xl font-bold text-yellow-600">{stats.pending_payments}</p>
                    </div>
                    <div className="border rounded p-4">
                      <h3 className="font-semibold mb-2">MoreLife Sessions</h3>
                      <p className="text-3xl font-bold text-purple-600">{stats.morelife_sessions}</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conference Registrations Tab */}
          <TabsContent value="registrations">
            <Card>
              <CardHeader>
                <div className="flex justify-between items-center">
                  <CardTitle>All Registrations & Purchases</CardTitle>
                  <div className="flex gap-2">
                    <Button onClick={() => exportToCSV(confRegistrations, 'conference-registrations')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Conferences
                    </Button>
                    <Button onClick={() => exportToCSV(bookPurchases, 'book-purchases')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Books
                    </Button>
                    <Button onClick={() => exportToCSV(trainingRegistrations, 'training-registrations')}>
                      <Download className="w-4 h-4 mr-2" />
                      Export Trainings
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs defaultValue="conf-reg">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="conf-reg">Conference ({confRegistrations.length})</TabsTrigger>
                    <TabsTrigger value="book-purch">Books ({bookPurchases.length})</TabsTrigger>
                    <TabsTrigger value="train-reg">Trainings ({trainingRegistrations.length})</TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="conf-reg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {confRegistrations.map((reg) => (
                          <TableRow key={reg.id}>
                            <TableCell>{reg.full_name}</TableCell>
                            <TableCell>{reg.email}</TableCell>
                            <TableCell>₦{reg.amount.toLocaleString()}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                reg.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {reg.payment_status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={reg.payment_status}
                                onValueChange={(value) => handleUpdatePaymentStatus('conference', reg.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabsContent>
                  
                  <TabsContent value="book-purch">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {bookPurchases.map((purchase) => (
                          <TableRow key={purchase.id}>
                            <TableCell>{purchase.full_name}</TableCell>
                            <TableCell>{purchase.user_email}</TableCell>
                            <TableCell>₦{purchase.amount.toLocaleString()}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                purchase.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {purchase.payment_status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={purchase.payment_status}
                                onValueChange={(value) => handleUpdatePaymentStatus('book', purchase.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabsContent>
                  
                  <TabsContent value="train-reg">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Name</TableHead>
                          <TableHead>Email</TableHead>
                          <TableHead>Amount</TableHead>
                          <TableHead>Status</TableHead>
                          <TableHead>Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {trainingRegistrations.map((reg) => (
                          <TableRow key={reg.id}>
                            <TableCell>{reg.full_name}</TableCell>
                            <TableCell>{reg.email}</TableCell>
                            <TableCell>₦{reg.amount.toLocaleString()}</TableCell>
                            <TableCell>
                              <span className={`px-2 py-1 rounded text-xs ${
                                reg.payment_status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                              }`}>
                                {reg.payment_status}
                              </span>
                            </TableCell>
                            <TableCell>
                              <Select
                                value={reg.payment_status}
                                onValueChange={(value) => handleUpdatePaymentStatus('training', reg.id, value)}
                              >
                                <SelectTrigger className="w-32">
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  <SelectItem value="pending">Pending</SelectItem>
                                  <SelectItem value="completed">Completed</SelectItem>
                                  <SelectItem value="failed">Failed</SelectItem>
                                </SelectContent>
                              </Select>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Conferences Tab - Will add CRUD operations */}
          <TabsContent value="conferences">
            <ConferencesManager 
              conferences={conferences}
              onUpdate={loadAllData}
              onExport={() => exportToCSV(conferences, 'conferences')}
            />
          </TabsContent>

          {/* Books Tab */}
          <TabsContent value="books">
            <BooksManager 
              books={books}
              onUpdate={loadAllData}
              onExport={() => exportToCSV(books, 'books')}
            />
          </TabsContent>

          {/* Trainings Tab */}
          <TabsContent value="trainings">
            <TrainingsManager 
              trainings={trainings}
              onUpdate={loadAllData}
              onExport={() => exportToCSV(trainings, 'trainings')}
            />
          </TabsContent>

          {/* Users Tab */}
          <TabsContent value="users">
            <UsersManager 
              users={users}
              onUpdate={loadAllData}
              onExport={() => exportToCSV(users, 'users')}
            />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default EnhancedDashboard;
